import {
	MotoringConviction as FormConviction,
	MotoringClaim as FormMotoringClaim,
} from '@acorn-portal/shared/src/services/api';
import { MotoringClaim, MotoringConviction, PcDriver } from '@acorn-portal/shared/src/services/subscriptionAPI';
import { capitalizeFirstLetter, generateDate } from '@acorn-portal/shared/src/utils';
import { DriverFormValues } from '@acorn-portal/shared/src/utils/initialValues/get-driver-form-values';
import { getLicenceNumber } from '@acorn-portal/shared/src/utils/transform/get-licence-number';
import { formatISO } from 'date-fns';
import IDate from '@acorn-portal/shared/src/utils/types/date';

const formatDate = (date: Date | string): string => formatISO(date, { representation: 'date' });
const formatDateFromObject = (date: IDate): string => formatDate(generateDate(date));

export const getMotoringConvictions = (motoringConvictions: FormConviction[]): MotoringConviction[] =>
	(motoringConvictions || []).map((conviction) => {
		const result: MotoringConviction = {
			convictionCode: conviction.offenceCode,
			disqualified: conviction.wasDisqualified,
		};
		if (conviction.dateOfConviction) result.convictionDate = formatDate(conviction.dateOfConviction);
		if (conviction.numberOfPoints !== undefined) result.points = conviction.numberOfPoints;
		if (conviction.fineAmount !== undefined) result.fine = conviction.fineAmount;
		if (conviction.isPending !== undefined) result.pending = conviction.isPending;
		if (conviction.dateOfOffence) result.offenceDate = formatDate(conviction.dateOfOffence);
		if (conviction.banStartDate) result.banStartDate = formatDate(conviction.banStartDate);
		if (conviction.banEndDate) result.banEndDate = formatDate(conviction.banEndDate);
		return result;
	});

export const getMotoringClaims = (motoringClaims: FormMotoringClaim[]): MotoringClaim[] =>
	(motoringClaims || []).map((claim) => ({
		incidentDate: formatDate(claim.dateOfClaim),
		claimType: claim.typeOfClaim,
		totalCosts: claim.costOfClaim,
		fault: claim.atFault,
		ncdAffected: claim.noClaimsBonusAffected,
		claimStatus: claim.statusOfClaim,
	}));

/**
 * Generates a new array of driver data, based on the provided DriverForm (full) values.
 *
 * This function is typically used to update working driver information in Redux state,
 * after a user edits driver details in the DriverForm.
 *
 * @param formValues - The values from the DriverForm.
 * @param driverIndex - The index of the driver to update in the driver data array.
 * @param storeData - The current array of driver data to be updated.
 * @returns A new array of driver data with the updated driver occupations.
 **/
export const createOrUpdateNewDriver = (
	formValues: DriverFormValues,
	driverIndex: number,
	storeData: PcDriver[],
): PcDriver[] => {
	// Clone data for immutability
	const updatedDrivers = structuredClone(storeData);
	const newDriver: PcDriver = {
		title: formValues.title,
		firstName: capitalizeFirstLetter(formValues.firstName),
		lastName: capitalizeFirstLetter(formValues.lastNames),
		mainDriver: storeData[driverIndex]?.mainDriver || false,
		dateOfBirth: formatDateFromObject(formValues.dateOfBirth),
		maritalStatus: formValues.maritalStatus,
		relationshipToProposer: formValues.relationshipToProposer,
		ukResidentSinceBirth: formValues.isResidentFromBirth === 'yes',
		dateUkResidency: formValues.ukResidenceSince ? formatDateFromObject(formValues.ukResidenceSince) : undefined,
		driverOccupations: [], // populated below
		licenceType: formValues.licenceType,
		licencePassedDate: formValues.licenceObtained ? formatDateFromObject(formValues.licenceObtained) : '',
		driverUsage: formValues.driverUsage ?? '',
		licenceNumber: getLicenceNumber(formValues.licenceNumber, formValues.licenceType),
		hasNonMotoringConvictions: formValues.hasCriminalConvictions === 'yes',
		motoringConvictions: getMotoringConvictions(formValues.motoringConvictions || []),
		hasInsuranceRefused: formValues.hasBeenRefusedCover === 'yes',
		accessToOtherVehicles: formValues.accessToOtherVehicles === 'yes',
		motoringClaims: getMotoringClaims(formValues.motoringClaims || []),
		isProposer: false,
	};

	const mainOccupation = {
		occupation: formValues.occupation,
		businessType: formValues.industry,
		employmentStatus: formValues.employmentType,
		isPartTime: formValues.employmentStatus === 'part-time',
		isFullTime: formValues.employmentStatus === 'full-time',
	};
	newDriver.driverOccupations[0] = mainOccupation;

	if (formValues.otherEmployment === 'yes') {
		const otherOccupation = {
			occupation: formValues.otherOccupation,
			businessType: formValues.otherIndustry,
			employmentStatus: formValues.otherEmploymentType,
			isPartTime: formValues.otherEmploymentStatus === 'part-time',
			isFullTime: formValues.otherEmploymentStatus === 'full-time',
		};
		newDriver.driverOccupations[1] = otherOccupation;
	}

	updatedDrivers[driverIndex] = newDriver;

	return updatedDrivers;
};
